import { FileText, Loader2 } from "lucide-react";
import "@google/model-viewer";
import type React from "react";
import { lazy, Suspense, useState } from "react";
import { isSameStoredObject } from "@/lib/presignedUrl";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { resolveViewerKind } from "./resolveViewerKind";

declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.HTMLAttributes<any> & {
          src?: string;
          alt?: string;
          poster?: string;
          loading?: "auto" | "lazy" | "eager";
          reveal?: "auto" | "interaction" | "manual";
          "auto-rotate"?: boolean;
          "auto-rotate-delay"?: number;
          "rotation-per-second"?: string;
          "camera-controls"?: boolean;
          "camera-orbit"?: string;
          "camera-target"?: string;
          "field-of-view"?: string;
          "min-camera-orbit"?: string;
          "max-camera-orbit"?: string;
          "min-field-of-view"?: string;
          "max-field-of-view"?: string;
          ar?: boolean;
          "ar-modes"?: string;
          "ar-scale"?: string;
          "ar-placement"?: string;
          "ios-src"?: string;
          "xr-environment"?: boolean;
          skybox?: string;
          "skybox-image"?: string;
          "environment-image"?: string;
          exposure?: number;
          "shadow-intensity"?: number;
          "shadow-softness"?: number;
          tone?: "commerce" | "neutral";
          animation?: string;
          "animation-name"?: string;
          "animation-crossfade-duration"?: number;
          autoplay?: boolean;
          "disable-zoom"?: boolean;
          "disable-pan"?: boolean;
          "disable-tap"?: boolean;
          "interaction-prompt"?: "auto" | "none";
          "interaction-prompt-style"?: "basic" | "wiggle";
          "interaction-prompt-threshold"?: number;
        },
        HTMLElement
      >;
    }
  }
}

const PDFViewer = lazy(() =>
  import("@/components/extend/pdf-viewer").then((module) => ({
    default: module.PDFViewer,
  })),
);

const DocxViewer = lazy(() =>
  import("@/components/extend/docx-viewer/DocxViewerPreview").then(
    (module) => ({
      default: module.DocxViewerPreview,
    }),
  ),
);

const XlsxViewer = lazy(() =>
  import("@/components/extend/xlsx-viewer/XlsxViewerPreview").then(
    (module) => ({
      default: module.XlsxViewerPreview,
    }),
  ),
);

const MarkdownFileViewer = lazy(() =>
  import("./MarkdownFileViewer").then((module) => ({
    default: module.MarkdownFileViewer,
  })),
);

const EmailFileViewer = lazy(() =>
  import("./EmailFileViewer").then((module) => ({
    default: module.EmailFileViewer,
  })),
);

// Fills the viewer area while a lazy viewer chunk downloads.
const viewerLoading = (
  <div className="flex h-full w-full items-center justify-center">
    <Loader2 className="h-4 w-4 animate-spin" />
  </div>
);

interface FileViewerProps {
  viewUrl: string;
  fileType: string;
  fileName: string;
  displayName: string;
  className?: string;
  viewControl?: React.ReactNode;
}

/**
 * Pure rendering component that displays files based on normalized props.
 * Supports Markdown, .eml emails, PDFs, images, 3D models, DOCX and XLSX with appropriate viewers.
 * PDFs, DOCX and XLSX open in the Extend viewers when the extend-ui-viewer flag
 * is on. When it is off, PDFs open in the browser's native viewer and office
 * files get the download link.
 * Falls back to a download link for unsupported file types.
 * Re-signed urls for the same stored object do not reload the embedded file.
 * Places viewControl in the Extend toolbar or floating over other previews.
 *
 * @param viewUrl - The URL to view or download the file
 * @param fileType - The MIME type of the file
 * @param fileName - The file name used for extension detection
 * @param displayName - The human-readable display name for the file
 * @param className - Optional CSS classes to apply to the viewer
 * @param viewControl - Optional control shown with the rendered file
 * @returns Appropriate viewer component based on file type
 */
export function FileViewer({
  viewUrl,
  fileType,
  fileName,
  displayName,
  className,
  viewControl,
}: FileViewerProps) {
  const kind = resolveViewerKind({ fileType, fileName });
  const loadedUrl = useLoadedUrl(viewUrl);
  const extendViewerEnabled = useFeatureFlag("extend-ui-viewer");
  // The office viewers own a night-render mode, toggled from their own menu.
  const [nightRender, setNightRender] = useState(false);
  const floatingControl = (position: string) =>
    viewControl ? (
      <div
        className={`absolute z-10 rounded-md border bg-background/90 shadow-sm ${position}`}
      >
        {viewControl}
      </div>
    ) : null;
  const downloadFallback = (
    <div
      className={`flex flex-col items-center justify-center h-full p-8 text-center ${className || ""}`}
    >
      <FileText className="h-16 w-16 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-2">{displayName}</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Preview not available for this file type
      </p>
      <a
        href={viewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary hover:underline"
      >
        Download File
      </a>
    </div>
  );

  switch (kind) {
    case "markdown":
      return (
        <div className={`relative w-full h-full ${className || ""}`}>
          <Suspense fallback={viewerLoading}>
            <MarkdownFileViewer key={loadedUrl} src={loadedUrl} />
          </Suspense>
          {floatingControl("right-2 top-2")}
        </div>
      );
    case "email":
      return (
        <div className={`relative w-full h-full ${className || ""}`}>
          <Suspense fallback={viewerLoading}>
            <EmailFileViewer key={loadedUrl} src={loadedUrl} />
          </Suspense>
          {floatingControl("right-2 top-2")}
        </div>
      );
    case "pdf":
      if (!extendViewerEnabled) {
        return (
          <div className={`relative w-full h-full ${className || ""}`}>
            <object
              data={`${loadedUrl}#view=FitH`}
              type="application/pdf"
              className="w-full h-full"
            >
              <p className="text-center p-4">
                Your browser doesn't support PDF viewing.
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline ml-1"
                >
                  Click here to download the PDF
                </a>
              </p>
            </object>
            {floatingControl("bottom-2 left-2")}
          </div>
        );
      }
      return (
        <div className={`w-full h-full ${className || ""}`}>
          <Suspense fallback={viewerLoading}>
            <PDFViewer
              src={loadedUrl}
              fileName={displayName}
              defaultZoom="fit-width"
              showUpload={false}
              showDownload={true}
              toolbarActions={viewControl}
              className="h-full w-full"
            />
          </Suspense>
        </div>
      );
    case "docx":
      if (!extendViewerEnabled) {
        return downloadFallback;
      }
      return (
        <div className={`w-full h-full ${className || ""}`}>
          <Suspense fallback={viewerLoading}>
            <DocxViewer
              src={loadedUrl}
              fileName={displayName}
              defaultZoom="fit-width"
              isDark={nightRender}
              onIsDarkChange={setNightRender}
              showUpload={false}
              showDownload={true}
              toolbarActions={viewControl}
              className="h-full w-full"
            />
          </Suspense>
        </div>
      );
    case "xlsx":
      if (!extendViewerEnabled) {
        return downloadFallback;
      }
      return (
        <div className={`w-full h-full ${className || ""}`}>
          <Suspense fallback={viewerLoading}>
            <XlsxViewer
              src={loadedUrl}
              fileName={displayName}
              isDark={nightRender}
              onIsDarkChange={setNightRender}
              showUpload={false}
              showDownload={true}
              toolbarActions={viewControl}
              className="h-full w-full"
            />
          </Suspense>
        </div>
      );
    case "image":
      return (
        <div className={`relative w-full h-full ${className || ""}`}>
          <img
            src={loadedUrl}
            alt={displayName}
            className="w-full h-full object-contain bg-background"
          />
          {floatingControl("right-2 top-2")}
        </div>
      );
    case "model":
      return (
        <div className={`relative w-full h-full ${className || ""}`}>
          <model-viewer
            src={loadedUrl}
            alt={displayName}
            auto-rotate={true}
            camera-controls={true}
            tone="neutral"
            environment-image="neutral"
            className="w-full h-full"
            style={{
              width: "100%",
              height: "100%",
              filter: "grayscale(100%) brightness(0.8)",
            }}
          />
          {floatingControl("right-2 top-2")}
        </div>
      );
    case "unsupported":
      return downloadFallback;
  }
}

/**
 * Keeps the url already loaded for a stored object across re-signs so the
 * browser does not reload it.
 */
function useLoadedUrl(viewUrl: string): string {
  const [loadedUrl, setLoadedUrl] = useState(viewUrl);
  if (isSameStoredObject(loadedUrl, viewUrl)) {
    return loadedUrl;
  }
  setLoadedUrl(viewUrl);
  return viewUrl;
}
